<script lang="ts" setup>
import { uniqWith } from "lodash-es";
import { nextTick, ref, useId, watch } from "vue";

import { ARRAY_LENGTH, REQUIRED } from "../../../composables/validation";
import { Platform } from "../../../enums";
import { AuditEnvironment } from "../../../types";
import DsfrField from "../../ui/DsfrField.vue";
import DsfrFieldWithValidation from "../../validation/DsfrFieldWithValidation.vue";
import FieldValidation from "../../validation/FieldValidation.vue";
import AuditEnvironmentCheckbox from "../AuditEnvironmentCheckbox.vue";
import {
  desktopCombinations,
  getCustomEnvironments,
  getDesktopCombinations,
  getMobileCombinations,
  mobileCombinations
} from "./combinations";

const props = defineProps<{
  modelValue: Omit<AuditEnvironment, "id">[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", payload: Omit<AuditEnvironment, "id">[]): void;
}>();

/** List of selected desktop environement combinations */
const selectedDesktopEnvironments = ref<string[]>(
  getDesktopCombinations(props.modelValue)
);

/** List of selected mobile environement combinations */
const selectedMobileEnvironments = ref<string[]>(
  getMobileCombinations(props.modelValue)
);

/** List of custom environments */
const customEnvironments = ref(getCustomEnvironments(props.modelValue));

watch(
  [customEnvironments, selectedDesktopEnvironments, selectedMobileEnvironments],
  ([
    customEnvironments,
    selectedDesktopEnvironments,
    selectedMobileEnvironments
  ]) => {
    const result = combineEnvironments(
      customEnvironments,
      selectedDesktopEnvironments,
      selectedMobileEnvironments
    );
    emit("update:modelValue", result);
  },
  {
    deep: true
  }
);

const envPlatformRefs = ref<InstanceType<typeof DsfrField>[]>([]);
const addEnvironmentButtonRef = ref<HTMLButtonElement>();

/**
 * Create a new environment and focus its platform field.
 */
async function addEnvironment() {
  customEnvironments.value.push({
    platform: "",
    operatingSystem: "",
    assistiveTechnology: "",
    browser: ""
  });
  await nextTick();
  const lastInput = envPlatformRefs.value[envPlatformRefs.value.length - 1];
  lastInput.inputRef?.focus();
}

/**
 * Delete environment at index and focus previous platform field or add button.
 * @param {number} i
 */
async function deleteEnvironment(i: number) {
  customEnvironments.value.splice(i, 1);
  await nextTick();
  const previousInput =
    i === 0 ? envPlatformRefs.value[0] : envPlatformRefs.value[i - 1];

  if (previousInput) {
    previousInput.inputRef?.focus();
  } else {
    addEnvironmentButtonRef.value?.focus();
  }
}

/**
 * Combine suggested desktop and mobile environments with custom ones.
 */
function combineEnvironments(
  customEnvironments: Omit<AuditEnvironment, "id">[],
  selectedDesktopEnvironments: string[],
  selectedMobileEnvironments: string[]
): Omit<AuditEnvironment, "id">[] {
  const desktop = selectedDesktopEnvironments.length
    ? desktopCombinations
        .filter((combination) => {
          return selectedDesktopEnvironments.includes(combination.title);
        })
        .map((combination) => {
          return combination.environments.map((c) => {
            return {
              // FIXME: set platform directly in desktopCombinations
              platform: Platform.DESKTOP,
              ...c
            };
          });
        })
        .flat(2)
    : [];

  const mobile = selectedMobileEnvironments.length
    ? mobileCombinations
        .filter((combination) => {
          return selectedMobileEnvironments.includes(combination.title);
        })
        .map((combination) => {
          return combination.environments.map((c) => {
            return {
              platform: Platform.MOBILE,
              ...c
            };
          });
        })
        .flat(2)
    : [];

  const filteredCustomEnvironments = customEnvironments.filter(
    (env) =>
      env.assistiveTechnology &&
      env.browser &&
      env.platform &&
      env.operatingSystem
  );

  return uniqWith(
    [...filteredCustomEnvironments, ...desktop, ...mobile],
    (a, b) => {
      return (
        a.platform === b.platform &&
        a.operatingSystem === b.operatingSystem &&
        a.browser === b.browser &&
        a.assistiveTechnology === b.assistiveTechnology
      );
    }
  );
}

const platformValidation = [REQUIRED("Champ obligatoire. Saisissez l’appareil")];
const osValidation = [REQUIRED("Champ obligatoire. Saisissez le logiciel d’exploitation.")];
const atValidation = [REQUIRED("Champ obligatoire. Saisissez la technologie d’assistance.")];
const browserValidation = [REQUIRED("Champ obligatoire. Saisissez le navigateur.")];

const uniqueId = useId();
const errorId = "error-" + uniqueId;
</script>

<template>
  <FieldValidation
    v-slot="{ error, focusRef }"
    :validation="[ARRAY_LENGTH(1, 'Information obligatoire. Indiquez les environnements de test.')]"
    :value="modelValue"
  >
    <div
      :ref="focusRef"
      class="fr-input-group fr-mb-6w"
      :class="{ 'fr-input-group--error': error }"
      role="region"
      :aria-describedby="error ? errorId : undefined"
      aria-labelledby="test-environment-section-title"
      tabindex="-1"
    >
      <div class="narrow-content">
        <h2 id="test-environment-section-title" class="fr-h4 fr-mb-2w">Environnements de test</h2>
        <p class="fr-text--sm">
          Nous vous proposons par défaut les combinaisons d’environnements de test prévus par le RGAA. Vous pouvez ajouter d’autres environnements de test.
        </p>
      </div>

      <div class="fr-mb-3w suggested-environments">
        <AuditEnvironmentCheckbox
          v-for="env in desktopCombinations"
          :key="env.title"
          v-model="selectedDesktopEnvironments"
          :value="env.title"
          :platform="Platform.DESKTOP"
          :title="env.title"
          :combinations="env.environments"
        />
      </div>

      <div class="fr-mb-3w suggested-environments">
        <AuditEnvironmentCheckbox
          v-for="env in mobileCombinations"
          :key="env.title"
          v-model="selectedMobileEnvironments"
          :value="env.title"
          :platform="Platform.MOBILE"
          :title="env.title"
          :combinations="env.environments"
        />
      </div>

      <h3 class="fr-text--lg fr-mb-5v">Environnements de test personnalisés</h3>

      <div v-if="customEnvironments.length" class="fr-mb-3w custom-environment">
        <fieldset
          v-for="(env, i) in customEnvironments"
          :key="i"
          class="fr-p-3w fr-m-0 env-card"
        >
          <legend class="env-legend">
            <h3 class="fr-h6 fr-mb-0">Environnement {{ i + 1 }}</h3>
          </legend>
          <button
            class="fr-btn fr-btn--tertiary-no-outline env-delete-button"
            type="button"
            @click="deleteEnvironment(i)"
          >
            Supprimer
          </button>

          <DsfrFieldWithValidation
            :id="`env-device-${i}`"
            ref="envPlatformRefs"
            v-model="env.platform"
            :validation="platformValidation"
            class="fr-m-0"
            label="Appareil"
            hint="Exemples : mobile, borne interactive"
            type="text"
            :required="customEnvironments.length > 1"
          />

          <DsfrFieldWithValidation
            :id="`env-os-${i}`"
            v-model="env.operatingSystem"
            :validation="osValidation"
            class="fr-m-0"
            label="Logiciel d’exploitation"
            hint="Exemple : macOS"
            type="text"
            :required="customEnvironments.length > 1"
          />

          <DsfrFieldWithValidation
            :id="`env-at-${i}`"
            v-model="env.assistiveTechnology"
            :validation="atValidation"
            class="fr-m-0"
            label="Technologie d’assistance"
            hint="Exemple : VoiceOver"
            type="text"
            :required="customEnvironments.length > 1"
          />

          <DsfrFieldWithValidation
            :id="`env-browser-${i}`"
            v-model="env.browser"
            :validation="browserValidation"
            label="Navigateur"
            hint="Exemple : Safari"
            type="text"
            :required="customEnvironments.length > 1"
          />
        </fieldset>
      </div>

      <button
        ref="addEnvironmentButtonRef"
        type="button"
        class="fr-btn fr-btn--secondary fr-btn--icon-left fr-icon-add-line"
        @click="addEnvironment"
      >
        Ajouter un environnement de test
      </button>

      <p v-if="error" :id="errorId" class="fr-error-text">
        {{ error }}
      </p>
    </div>
  </FieldValidation>
</template>

<style scoped>
.narrow-content {
  max-width: 49.5rem;
}

.custom-environment,
.suggested-environments {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.env-card {
  border: 1px solid var(--border-default-grey);
  display: grid;
  gap: 1rem;
  grid-template-columns: auto auto;
  align-items: center;
}

.env-legend {
  float: left;
}

.env-delete-button {
  justify-self: end;
}

.env-card > *:not(.env-legend, .env-delete-button) {
  grid-column: 1 / -1;
}

@media (width < 48rem) {
  .custom-environment,
  .suggested-environments {
    grid-template-columns: 1fr;
  }
}
</style>
